import { useGetAllMentors, useGetMentorActivityReport } from '@actions';
import apiClient from '@contexts/AuthContext/apiClient';
import SkeletonBox from '@components/V4/SkeletonBox';
import Table from '@components/V4/Table/Table';
import { useDebounce } from '@hooks';
import { Button, Select } from 'antd';
import moment from 'moment';
import { useState } from 'react';
import { DownloadIcon } from '@heroicons/react/outline';
import { logger } from 'apps/agora/src/utils/logger';

type PeriodKey = '3m' | '6m' | '12m' | 'all';

const PERIOD_OPTIONS: { label: string; value: PeriodKey }[] = [
  { label: 'Last 3 months', value: '3m' },
  { label: 'Last 6 months', value: '6m' },
  { label: 'Last 12 months', value: '12m' },
  { label: 'Since joining Upgrade', value: 'all' },
];

const PERIOD_MONTHS: Record<Exclude<PeriodKey, 'all'>, number> = {
  '3m': 3,
  '6m': 6,
  '12m': 12,
};

const getRangeStart = (period: PeriodKey) =>
  period === 'all'
    ? undefined
    : moment().subtract(PERIOD_MONTHS[period], 'months').startOf('day').toISOString();

const MentorActivityTab = () => {
  const [mentorId, setMentorId] = useState<string | undefined>();
  const [period, setPeriod] = useState<PeriodKey>('12m');
  const [mentorSearch, setMentorSearch] = useState('');

  const debouncedMentorSearch = useDebounce(mentorSearch, 500);

  const { data: mentors, isLoading: isLoadingMentors } = useGetAllMentors({
    search: debouncedMentorSearch,
    limit: 20,
  });

  const { data: report, isLoading } = useGetMentorActivityReport(
    { mentorId: mentorId as string, rangeStart: getRangeStart(period) },
    { enabled: !!mentorId }
  );

  const statusRows = report?.statuses ?? [];
  const monthRows = report?.months ?? [];

  const formatRate = (entry: typeof monthRows[number]) =>
    entry.rateAmount !== null
      ? `${entry.rateAmount.toFixed(2)} ${entry.rateCurrency ?? ''}`.trim()
      : 'No report';

  const formatAmount = (entry: typeof monthRows[number]) =>
    entry.amount !== null ? `${entry.amount.toFixed(2)} ${entry.rateCurrency ?? ''}`.trim() : 'n/a';

  const totalsLabel = (report?.totalsByCurrency ?? [])
    .map((total) => `${total.amount.toFixed(2)} ${total.currency}`)
    .join(' + ');

  const extraServicesLabel = (report?.extraServicesTotalsByCurrency ?? [])
    .map((total) => `${total.amount.toFixed(2)} ${total.currency}`)
    .join(' + ');

  const formatExtraServices = (entry: typeof monthRows[number]) =>
    entry.extraServicesAmount !== null
      ? `${entry.extraServicesAmount.toFixed(2)} ${entry.extraServicesCurrency ?? ''}`.trim()
      : '—';

  const monthsCount = monthRows.length;

  const perMonth = (value: number) => (monthsCount ? value / monthsCount : 0);

  const formatCount = (value: number) => value.toLocaleString('en-US');

  const formatAvgCount = (value: number) => (monthsCount ? perMonth(value).toFixed(1) : '—');

  const formatMoneyByCurrency = (entries: { currency: string; amount: number }[], divisor = 1) =>
    entries.length
      ? entries
          .map((entry) => `${(entry.amount / divisor).toFixed(2)} ${entry.currency}`)
          .join(' + ')
      : '—';

  const avgRateByCurrency = Object.entries(
    monthRows.reduce<Record<string, { sum: number; count: number }>>((acc, entry) => {
      if (entry.rateAmount === null) return acc;

      const currency = entry.rateCurrency ?? '';
      const bucket = acc[currency] ?? { sum: 0, count: 0 };
      bucket.sum += entry.rateAmount;
      bucket.count += 1;
      acc[currency] = bucket;

      return acc;
    }, {})
  ).map(([currency, bucket]) => ({ currency, amount: bucket.sum / bucket.count }));

  const summaryRows = [
    {
      label: 'Sessions held',
      total: formatCount(report?.totalSessions ?? 0),
      average: formatAvgCount(report?.totalSessions ?? 0),
    },
    {
      label: 'Completed',
      total: formatCount(report?.completedSessions ?? 0),
      average: formatAvgCount(report?.completedSessions ?? 0),
    },
    {
      label: 'Paid',
      total: formatCount(report?.paidSessions ?? 0),
      average: formatAvgCount(report?.paidSessions ?? 0),
    },
    {
      label: 'Unpayable',
      total: formatCount(report?.unpayableSessions ?? 0),
      average: formatAvgCount(report?.unpayableSessions ?? 0),
    },
    {
      label: 'Billable (completed + paid)',
      total: formatCount(report?.billableSessions ?? 0),
      average: formatAvgCount(report?.billableSessions ?? 0),
    },
    {
      label: 'Students taught',
      total: formatCount(report?.activeStudents ?? 0),
      average: '—',
    },
    {
      label: 'Rate at the time',
      total: '—',
      average: formatMoneyByCurrency(avgRateByCurrency),
    },
    {
      label: 'Earned',
      total: formatMoneyByCurrency(report?.totalsByCurrency ?? []),
      average: monthsCount
        ? formatMoneyByCurrency(report?.totalsByCurrency ?? [], monthsCount)
        : '—',
    },
    {
      label: 'Extra services',
      total: formatMoneyByCurrency(report?.extraServicesTotalsByCurrency ?? []),
      average: monthsCount
        ? formatMoneyByCurrency(report?.extraServicesTotalsByCurrency ?? [], monthsCount)
        : '—',
    },
  ];

  const [isDownloading, setIsDownloading] = useState(false);

  const downloadArchiveHandler = async () => {
    if (!mentorId) return;

    setIsDownloading(true);

    try {
      const response = await apiClient.post(
        '/reports/bulk/download-mentor-archive',
        { mentorId, rangeStart: getRangeStart(period) },
        { responseType: 'blob', timeout: 1000000 }
      );

      const url = URL.createObjectURL(new Blob([response.data], { type: 'application/zip' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${report?.mentor?.fullName ?? 'mentor'}-activity-${period}.zip`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      logger.error('Error downloading the mentor activity archive:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const statCards = [
    { value: report?.totalSessions ?? 0, subtitle: 'Sessions in period' },
    { value: report?.completedSessions ?? 0, subtitle: 'Completed' },
    { value: report?.unpayableSessions ?? 0, subtitle: 'Unpayable' },
    { value: report?.paidSessions ?? 0, subtitle: 'Paid' },
    { value: report?.activeStudents ?? 0, subtitle: 'Students taught' },
  ];

  return (
    <div style={{ width: '100%' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '16px',
          marginBottom: '24px',
        }}
      >
        <Select
          style={{ width: '280px' }}
          showSearch
          allowClear
          placeholder="Search a mentor"
          value={mentorId}
          filterOption={false}
          searchValue={mentorSearch}
          onSearch={setMentorSearch}
          onChange={(value) => setMentorId(value)}
          loading={isLoadingMentors}
          options={(mentors ?? []).map((mentor) => ({
            label: mentor.fullName,
            value: mentor._id,
          }))}
        />

        <Select
          style={{ width: '220px' }}
          value={period}
          onChange={(value: PeriodKey) => setPeriod(value)}
          options={PERIOD_OPTIONS}
        />

        <Button
          type="primary"
          disabled={!mentorId || isLoading || isDownloading}
          loading={isDownloading}
          icon={<DownloadIcon style={{ width: 16, height: 16, marginRight: 8 }} />}
          onClick={downloadArchiveHandler}
          style={{ display: 'flex', alignItems: 'center' }}
        >
          {isDownloading ? 'Preparing archive...' : 'Download reports & invoices'}
        </Button>
      </div>

      {!mentorId ? (
        <p style={{ textAlign: 'center', color: '#9CA3AF' }}>
          Pick a mentor to see everything they held in the selected period, broken down by session
          status. Onboarding and education strategy meetings are included, exactly as they are in
          the mentor&apos;s own activity report.
        </p>
      ) : (
        <>
          {isLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '16px' }}>
              <SkeletonBox className="h-4 w-72 rounded-lg" />
            </div>
          ) : (
            <p style={{ textAlign: 'center', color: '#9CA3AF', marginBottom: '16px' }}>
              {`${report?.mentor?.fullName ?? 'Mentor'} · ${moment(report?.rangeStart).format(
                'DD.MM.YYYY'
              )} – ${moment(report?.rangeEnd).format('DD.MM.YYYY')}`}
            </p>
          )}

          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '24px',
              alignItems: 'flex-start',
              justifyContent: 'center',
            }}
          >
            <div style={{ flex: '1 1 420px', maxWidth: '520px' }}>
              <h4 style={{ marginBottom: '8px' }}>
                Summary{monthsCount ? ` · ${monthsCount} month${monthsCount === 1 ? '' : 's'}` : ''}
              </h4>
              <Table size="small" isLoading={isLoading}>
                <Table.Header>
                  <Table.Row isHeader>
                    <Table.HeaderCell>Metric</Table.HeaderCell>
                    <Table.HeaderCell>Total</Table.HeaderCell>
                    <Table.HeaderCell>Avg / month</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body hasResults={!!monthsCount}>
                  {summaryRows.map((row) => (
                    <Table.Row key={row.label}>
                      <Table.Cell>{row.label}</Table.Cell>
                      <Table.Cell className="tabular-nums text-white">{row.total}</Table.Cell>
                      <Table.Cell className="tabular-nums text-customPrimary">
                        {row.average}
                      </Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            <div style={{ flex: '1 1 320px', maxWidth: '420px' }}>
              <h4 style={{ marginBottom: '8px' }}>By session status</h4>
              <Table size="small" isLoading={isLoading}>
                <Table.Header>
                  <Table.Row isHeader>
                    <Table.HeaderCell>Session status</Table.HeaderCell>
                    <Table.HeaderCell>Sessions</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body hasResults={!!statusRows.length}>
                  {statusRows.map((entry) => (
                    <Table.Row key={entry.status}>
                      <Table.Cell>{entry.label}</Table.Cell>
                      <Table.Cell>{entry.count}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>

            <div style={{ flex: '1 1 100%' }}>
              <h4 style={{ marginBottom: '8px' }}>By month</h4>
              <p style={{ color: '#9CA3AF', marginBottom: '8px' }}>
                Completed and paid sessions are priced with the rate frozen on that month&apos;s
                activity report, VAT included where the mentor is a VAT payer. Months without a
                generated report show no rate. Extra services are the ones bundled into that
                month&apos;s report, in the invoice currency.
                {report?.unpricedSessions
                  ? ` ${report.unpricedSessions} session(s) could not be priced.`
                  : ''}
              </p>
              <Table size="small" isLoading={isLoading}>
                <Table.Header>
                  <Table.Row isHeader>
                    <Table.HeaderCell>Month</Table.HeaderCell>
                    <Table.HeaderCell>Sessions</Table.HeaderCell>
                    <Table.HeaderCell>Completed</Table.HeaderCell>
                    <Table.HeaderCell>Paid</Table.HeaderCell>
                    <Table.HeaderCell>Unpayable</Table.HeaderCell>
                    <Table.HeaderCell>Rate at the time</Table.HeaderCell>
                    <Table.HeaderCell>Amount</Table.HeaderCell>
                    <Table.HeaderCell>Extra services</Table.HeaderCell>
                  </Table.Row>
                </Table.Header>
                <Table.Body hasResults={!!monthRows.length}>
                  {monthRows.map((entry) => (
                    <Table.Row key={entry.month}>
                      <Table.Cell>{moment(entry.month, 'YYYY-MM').format('MMMM YYYY')}</Table.Cell>
                      <Table.Cell>{entry.total}</Table.Cell>
                      <Table.Cell>{entry.completed}</Table.Cell>
                      <Table.Cell>{entry.paid}</Table.Cell>
                      <Table.Cell>{entry.unpayable}</Table.Cell>
                      <Table.Cell>{formatRate(entry)}</Table.Cell>
                      <Table.Cell>{formatAmount(entry)}</Table.Cell>
                      <Table.Cell>{formatExtraServices(entry)}</Table.Cell>
                    </Table.Row>
                  ))}
                </Table.Body>
              </Table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MentorActivityTab;
